/* eslint-disable no-undef */
import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import { TreeSelect, Select, Tag, Spin } from 'antd';
import { GlobalOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { useIsMobile } from '@client/hooks/useIsMobile';
import { MobileTreeSheet } from '@client/components/mobile';

// ============================================================================
// CONSTANTS
// ============================================================================
const DEFAULTS = {
  PLACEHOLDER: 'Select an option',
  LANGUAGE: 'fr',
};

const CONFIG = {
  DROPDOWN_MAX_HEIGHT: 400,
  DROPDOWN_PADDING: '8px 0',
  CHEVRON_SIZE: 14,
  CACHE_DURATION: 15 * 60 * 1000, // 15 minutes cache
  SEARCH_DEBOUNCE: 300, // ms
  MIN_SEARCH_LENGTH: 2,
};

// Stable empty object to prevent infinite re-renders
const EMPTY_FILTER_PARAMS = {};

// API endpoint for the new optimized backend
const CALCULATOR_TREE_API = '/api/cultures/tree/calculator/';

// ============================================================================
// GLOBAL CACHE - Persists across component mounts and step navigations
// ============================================================================
const globalCache = {
  // Full tree cache per language
  fullTrees: new Map(),
  // Timestamp for cache validation
  timestamp: null,
};

// Generate cache key from language and filter params
const getCacheKey = (lang, filterParams = {}) => {
  const filterStr = Object.entries(filterParams)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([k, v]) => `${k}=${v}`)
    .join('&');
  return filterStr ? `${lang}:${filterStr}` : lang;
};

const getCachedFullTree = (lang, filterParams = {}) => {
  const cacheKey = getCacheKey(lang, filterParams);
  const cached = globalCache.fullTrees.get(cacheKey);
  if (cached && globalCache.timestamp && Date.now() - globalCache.timestamp < CONFIG.CACHE_DURATION) {
    return cached;
  }
  return null;
};

const setCachedFullTree = (lang, filterParams = {}, data) => {
  const cacheKey = getCacheKey(lang, filterParams);
  globalCache.fullTrees.set(cacheKey, data);
  globalCache.timestamp = Date.now();
};

// ============================================================================
// LANGUAGE SELECTOR COMPONENT
// ============================================================================
const LanguageSelector = React.memo(({ value, options, onChange }) => (
  <Select
    size="small"
    value={value}
    onChange={onChange}
    options={options}
    className="language-selector"
    suffixIcon={<GlobalOutlined />}
  />
));
LanguageSelector.displayName = 'LanguageSelector';

// ============================================================================
// MAIN COMPONENT
// ============================================================================
const CultureTreeSelect = ({
  label,
  endpoint: _endpoint, // eslint-disable-line no-unused-vars -- Ignored, we use the new optimized API
  placeholder = DEFAULTS.PLACEHOLDER,
  isMulti = false,
  value,
  onChange,
  style,
  checkStrictly = false,
  languageFields = null,
  languageLabels = null,
  defaultLanguage = DEFAULTS.LANGUAGE,
  showLanguageSelector = false,
  onLanguageChange = null,
  maxCount = null,
  treeCheckable = true,
  enrichedCultures = null, // For backward compatibility (URL restore)
  filterParams = EMPTY_FILTER_PARAMS, // Additional filter parameters to pass to the API (e.g., { show_in_customer_filter_multi_processing: true })
}) => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();

  // ============================================================================
  // STATE
  // ============================================================================
  const [treeData, setTreeData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchValue, setSearchValue] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expandedKeys, setExpandedKeys] = useState([]);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState(() => {
    if (!languageFields) return defaultLanguage;
    const saved = localStorage.getItem(`lang_calculator_culture`);
    return saved || defaultLanguage;
  });

  // Refs
  const searchDebounceRef = useRef(null);
  const abortControllerRef = useRef(null);

  // ============================================================================
  // COMPUTED VALUES
  // ============================================================================
  const languageOptions = useMemo(() => {
    if (!languageLabels) return [];
    return Object.entries(languageLabels).map(([code, lbl]) => ({
      value: code,
      label: lbl,
    }));
  }, [languageLabels]);

  // Build a flat map for O(1) label lookup from tree data
  const nodeMap = useMemo(() => {
    const map = new Map();

    const addNodes = (nodes) => {
      if (!nodes) return;
      nodes.forEach((node) => {
        const id = node.value || node.id;
        if (id) map.set(id, node);
        if (node.children?.length > 0) addNodes(node.children);
      });
    };

    // Add from tree data
    addNodes(treeData);

    // Add from search results if any
    if (searchResults) {
      searchResults.forEach((node) => {
        const id = node.value || node.id;
        if (id && !map.has(id)) map.set(id, node);
      });
    }

    // Add from enrichedCultures (backward compatibility)
    if (enrichedCultures?.length > 0) {
      enrichedCultures.forEach((culture) => {
        const id = culture.value || culture.id;
        if (id && !map.has(id)) {
          map.set(id, {
            ...culture,
            value: id,
            title: culture.title || culture.label || culture.french_name || id,
          });
        }
      });
    }

    return map;
  }, [treeData, searchResults, enrichedCultures]);

  // ============================================================================
  // API HELPERS
  // ============================================================================
  const fetchFromAPI = useCallback(async (params, signal) => {
    const url = new URL(CALCULATOR_TREE_API, window.location.origin);

    // Add base params
    Object.entries(params).forEach(([key, val]) => {
      if (val !== undefined && val !== null && val !== '') {
        url.searchParams.set(key, val);
      }
    });

    // Add filter params
    Object.entries(filterParams).forEach(([key, val]) => {
      if (val !== undefined && val !== null && val !== '') {
        url.searchParams.set(key, val);
      }
    });

    const response = await fetch(url.toString(), { signal });
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    const result = await response.json();
    return result.data || [];
  }, [filterParams]);

  // ============================================================================
  // LOAD FULL TREE (on mount)
  // ============================================================================
  const loadFullTree = useCallback(async () => {
    // Check cache first (include filterParams in cache key)
    const cached = getCachedFullTree(selectedLanguage, filterParams);
    if (cached) {
      setTreeData(cached);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Cancel any pending request
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      abortControllerRef.current = new AbortController();

      const data = await fetchFromAPI(
        { mode: 'full', lang: selectedLanguage },
        abortControllerRef.current.signal
      );

      setTreeData(data);
      setCachedFullTree(selectedLanguage, filterParams, data);
    } catch (err) {
      if (err.name !== 'AbortError') {
        setError(err.message);
      }
    } finally {
      setLoading(false);
    }
  }, [selectedLanguage, filterParams, fetchFromAPI]);

  // Load full tree on mount and language change
  useEffect(() => {
    loadFullTree();
  }, [loadFullTree]);

  // ============================================================================
  // SEARCH (backend search with debounce)
  // ============================================================================
  const performSearch = useCallback(async (term) => {
    if (!term || term.length < CONFIG.MIN_SEARCH_LENGTH) {
      setSearchResults(null);
      setSearchLoading(false);
      return;
    }

    setSearchLoading(true);

    try {
      // Cancel any pending request
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      abortControllerRef.current = new AbortController();

      const data = await fetchFromAPI(
        { mode: 'search', search: term, lang: selectedLanguage },
        abortControllerRef.current.signal
      );

      setSearchResults(data);
    } catch (err) {
      if (err.name !== 'AbortError') {
        setSearchResults([]);
      }
    } finally {
      setSearchLoading(false);
    }
  }, [selectedLanguage, fetchFromAPI]);

  // Debounced search handler
  const handleSearch = useCallback((term) => {
    setSearchValue(term);

    // Clear previous debounce
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }

    if (!term || term.length < CONFIG.MIN_SEARCH_LENGTH) {
      setSearchResults(null);
      return;
    }

    // Debounce the search
    searchDebounceRef.current = setTimeout(() => {
      performSearch(term);
    }, CONFIG.SEARCH_DEBOUNCE);
  }, [performSearch]);

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  // ============================================================================
  // EVENT HANDLERS
  // ============================================================================
  const handleChange = useCallback((selectedValues) => {
    if (!onChange) return;

    const validValues = Array.isArray(selectedValues)
      ? selectedValues
      : [selectedValues].filter(Boolean);

    // Build enriched nodes from nodeMap
    const enrichedNodes = validValues.map((val) => {
      const node = nodeMap.get(val);
      return {
        value: val,
        label: node?.title || val,
        key: val,
        title: node?.title || val,
        french_name: node?.french_name,
        english_name: node?.english_name,
        latin_name: node?.latin_name,
        ...(node || {}),
      };
    });

    onChange(validValues, enrichedNodes);

    // Clear search after selection
    if (searchValue) {
      setSearchValue('');
      setSearchResults(null);
    }
  }, [onChange, nodeMap, searchValue]);

  const handleLanguageChange = useCallback((lang) => {
    setSelectedLanguage(lang);
    localStorage.setItem(`lang_calculator_culture`, lang);
    setSearchValue('');
    setSearchResults(null);

    // Clear selections when language changes
    if (onChange) onChange([], []);
    if (onLanguageChange) setTimeout(onLanguageChange, 100);
  }, [onChange, onLanguageChange]);

  const handleDropdownVisibleChange = useCallback((visible) => {
    setDropdownOpen(visible);
    if (!visible) {
      // Clear search when dropdown closes
      setSearchValue('');
      setSearchResults(null);
    }
  }, []);

  // ============================================================================
  // TAG RENDER (resolve labels for selected items)
  // ============================================================================
  const tagRender = useCallback((props) => {
    const { label: tagLabel, value: tagValue, closable, onClose } = props;

    // Try to get proper label from nodeMap
    const node = nodeMap.get(tagValue);
    const displayLabel = node?.title || tagLabel || tagValue;

    return (
      <Tag closable={closable} onClose={onClose} style={{ marginRight: 3 }}>
        {displayLabel}
      </Tag>
    );
  }, [nodeMap]);

  // ============================================================================
  // FILTER (client-side filter for tree display)
  // ============================================================================
  const filterTreeNode = useCallback((inputValue, treeNode) => {
    // When using backend search, don't filter client-side
    if (searchResults !== null) return true;

    if (!inputValue) return true;

    const searchLower = inputValue.toLowerCase();
    const title = String(treeNode.title || '').toLowerCase();
    const frenchName = String(treeNode.french_name || '').toLowerCase();
    const englishName = String(treeNode.english_name || '').toLowerCase();
    const latinName = String(treeNode.latin_name || '').toLowerCase();

    return (
      title.includes(searchLower) ||
      frenchName.includes(searchLower) ||
      englishName.includes(searchLower) ||
      latinName.includes(searchLower)
    );
  }, [searchResults]);

  // ============================================================================
  // RENDER HELPERS
  // ============================================================================
  const notFoundContent = useMemo(() => {
    if (error) return error;
    if (loading || searchLoading) return <Spin size="small" />;
    if (searchResults !== null && searchResults.length === 0) {
      return t('common|No results found');
    }
    return t('common|No options found');
  }, [error, loading, searchLoading, searchResults, t]);

  const switcherIcon = useCallback(({ expanded }) => (
    expanded
      ? <ChevronDown size={CONFIG.CHEVRON_SIZE} />
      : <ChevronRight size={CONFIG.CHEVRON_SIZE} />
  ), []);

  // Custom title renderer for styling parent nodes differently
  const treeTitleRender = useCallback((node) => {
    const hasChildren = node.children && node.children.length > 0;

    if (hasChildren) {
      // Parent node styling - green, bold, 16px
      return (
        <span
          style={{
            color: '#207D55',
            fontFamily: 'Roboto',
            fontSize: '16px',
            fontWeight: 700,
            lineHeight: '125%',
          }}
        >
          {node.title}
        </span>
      );
    }

    // Child node - default styling
    return <span>{node.title}</span>;
  }, []);

  // Transform tree data to disable checkboxes on parent nodes
  const transformTreeData = useCallback((nodes) => {
    if (!nodes || !Array.isArray(nodes)) return nodes;

    return nodes.map((node) => {
      const hasChildren = node.children && node.children.length > 0;

      return {
        ...node,
        // Disable checkbox for parent nodes (nodes with children)
        checkable: !hasChildren,
        disableCheckbox: hasChildren,
        selectable: !hasChildren, // Only leaf nodes are selectable
        // Recursively transform children
        children: hasChildren ? transformTreeData(node.children) : node.children,
      };
    });
  }, []);

  // Use search results if available, otherwise use full tree
  const displayTreeData = useMemo(() => {
    const data = searchResults !== null ? searchResults : treeData;
    return transformTreeData(data);
  }, [searchResults, treeData, transformTreeData]);

  // ============================================================================
  // MOBILE HELPERS
  // ============================================================================
  // Get display text for mobile trigger
  const mobileDisplayValue = useMemo(() => {
    if (!value || (Array.isArray(value) && value.length === 0)) {
      return '';
    }

    const valueArray = Array.isArray(value) ? value : [value];

    if (isMulti) {
      const count = valueArray.length;
      if (count === 0) return '';
      if (count === 1) {
        const node = nodeMap.get(valueArray[0]);
        return node?.title || valueArray[0];
      }
      return t('riskCalculator|mobile_count_selected', { count });
    }

    const node = nodeMap.get(valueArray[0]);
    return node?.title || valueArray[0];
  }, [value, nodeMap, isMulti, t]);

  // Language selector for mobile sheet
  const mobileLanguageSelector = useMemo(() => {
    if (!showLanguageSelector || !languageFields || !languageLabels) return null;

    return (
      <LanguageSelector
        value={selectedLanguage}
        options={languageOptions}
        onChange={handleLanguageChange}
      />
    );
  }, [showLanguageSelector, languageFields, languageLabels, selectedLanguage, languageOptions, handleLanguageChange]);

  // ============================================================================
  // RENDER
  // ============================================================================

  // Mobile render
  if (isMobile) {
    return (
      <div className="w-full space-y-2">
        <div className="select-header flex items-center justify-between">
          {label && (
            <label className="block text-sm font-medium text-gray-700">
              {t(label)}
            </label>
          )}
          {showLanguageSelector && languageFields && languageLabels && (
            <LanguageSelector
              value={selectedLanguage}
              options={languageOptions}
              onChange={handleLanguageChange}
            />
          )}
        </div>

        {/* Mobile trigger - styled like a select */}
        <div
          onClick={() => setSheetOpen(true)}
          style={{
            width: '100%',
            minHeight: 44,
            padding: '8px 12px',
            border: '1px solid #d9d9d9',
            borderRadius: 6,
            backgroundColor: '#fff',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            color: mobileDisplayValue ? '#262626' : '#bfbfbf',
            ...(style || {}),
          }}
        >
          <span
            style={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              flex: 1,
            }}
          >
            {mobileDisplayValue || placeholder}
          </span>
          {loading ? (
            <Spin size="small" style={{ marginLeft: 8 }} />
          ) : (
            <ChevronDown size={16} style={{ marginLeft: 8, color: '#bfbfbf', flexShrink: 0 }} />
          )}
        </div>

        {/* Mobile tree sheet */}
        <MobileTreeSheet
          isOpen={sheetOpen}
          onClose={() => setSheetOpen(false)}
          title={label ? t(label) : placeholder}
          treeData={displayTreeData}
          value={Array.isArray(value) ? value : (value ? [value] : [])}
          onChange={handleChange}
          isMulti={isMulti}
          loading={loading || searchLoading}
          onSearch={handleSearch}
          maxCount={maxCount}
          languageSelector={mobileLanguageSelector}
        />
      </div>
    );
  }

  // Desktop render (unchanged)
  return (
    <div className="w-full space-y-2">
      <style>
        {`
          .culture-tree-select-dropdown .ant-select-tree-checkbox-checked .ant-select-tree-checkbox-inner {
            background-color: #207D55 !important;
            border-color: #207D55 !important;
          }
          .culture-tree-select-dropdown .ant-select-tree-checkbox-indeterminate .ant-select-tree-checkbox-inner::after {
            background-color: #207D55 !important;
          }
          .culture-tree-select-dropdown .ant-select-tree-checkbox:hover .ant-select-tree-checkbox-inner {
            border-color: #207D55 !important;
          }
          .culture-tree-select-dropdown .ant-select-tree-checkbox-checked::after {
            border-color: #207D55 !important;
          }
        `}
      </style>
      <div className="select-header flex items-center justify-between">
        {label && (
          <label className="block text-sm font-medium text-gray-700">
            {t(label)}
          </label>
        )}
        {showLanguageSelector && languageFields && languageLabels && (
          <LanguageSelector
            value={selectedLanguage}
            options={languageOptions}
            onChange={handleLanguageChange}
          />
        )}
      </div>

      <TreeSelect
        key={`${selectedLanguage}-${nodeMap.size}`}
        value={value}
        onChange={handleChange}
        treeData={displayTreeData}
        expandedKeys={expandedKeys}
        onExpand={setExpandedKeys}
        open={dropdownOpen}
        onDropdownVisibleChange={handleDropdownVisibleChange}
        treeNodeFilterProp="title"
        fieldNames={{
          label: 'title',
          value: 'value',
          children: 'children',
          key: 'value',
        }}
        switcherIcon={switcherIcon}
        treeTitleRender={treeTitleRender}
        placeholder={placeholder}
        loading={loading || searchLoading}
        showSearch
        allowClear
        multiple={isMulti}
        treeDefaultExpandAll={false}
        style={style}
        className="culture-tree-select"
        dropdownStyle={{
          maxHeight: CONFIG.DROPDOWN_MAX_HEIGHT,
          overflow: 'auto',
          padding: CONFIG.DROPDOWN_PADDING,
        }}
        dropdownClassName="culture-tree-select-dropdown"
        searchValue={searchValue}
        onSearch={handleSearch}
        filterTreeNode={filterTreeNode}
        notFoundContent={notFoundContent}
        treeCheckable={isMulti && treeCheckable}
        showCheckedStrategy={TreeSelect.SHOW_CHILD}
        popupMatchSelectWidth={false}
        checkStrictly={isMulti && checkStrictly}
        tagRender={isMulti ? tagRender : undefined}
        {...(isMulti && maxCount ? { maxCount } : {})}
      />
    </div>
  );
};

export default React.memo(CultureTreeSelect);
